import React, { useEffect, useCallback, useState } from 'react';
import { List, Row, Col, Typography, Select, Space, Card, Button } from 'antd';
import { useQuery } from '@apollo/client';
import { queries } from '../../graphql/graphql';
import { Loader } from '../../components/UI/Loader';

const { Text } = Typography;
const { Option } = Select;

const PAGE_SIZE = 20;

interface ProductType {
  value: string;
  label: string;
}

const PRODUCT_TYPES: ProductType[] = [
  { value: 'all', label: 'All Types' },
  { value: 'objects', label: 'Objects' },
  { value: 'materials', label: 'Materials' },
  { value: 'materials-options', label: 'Materials from options' },
];

interface ProductAvatar {
  small: string;
  large: string;
}

interface ProductData {
  price: number;
  labor: number;
  url: string;
}

interface Product {
  id: string;
  name?: string;
  type?: string;
  avatar?: ProductAvatar;
  data?: ProductData;
}

interface ProductListProps {
  products: Product[];
}

const ProductList: React.FC<ProductListProps> = ({ products }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedType, setSelectedType] = useState<string>('all');

  const handleOpenProduct = useCallback((id: string, type: string) => {
    if (type === 'materials-options') {
      window.open(`https://3d.homewip.com/#!/admin/configurator/`, '_blank');
    } else {
      window.open(`https://3d.homewip.com/#!/product/${id}`, '_blank');
    }
  }, []);

  const filteredProducts = products.filter(
    product => selectedType === 'all' || product.type?.toLowerCase() === selectedType
  );

  const handleTypeChange = (value: string) => {
    setSelectedType(value);
    setCurrentPage(1);
  };

  const renderProductItem = (item: Product) => (
    <List.Item key={item.id}>
      <Card
        cover={
          item.avatar?.small ? (
            <img
              src={item.avatar?.small}
              alt={item.name}
              style={{
                maxWidth: '100%',
                aspectRatio: 1 / 1,
                objectFit: 'cover',
              }}
            />
          ) : null
        }
        actions={[
          <Button
            key={`view-product-${item.id}`}
            onClick={() => item.id && handleOpenProduct(item.id, item.type || '')}
            type="link">
            View Product
          </Button>,
        ]}>
        <Card.Meta
          style={{ minHeight: '150px' }}
          title={item.name}
          description={
            <>
              <p>Type: {item.type}</p>
              {item.data && (
                <>
                  <p>Price: ${item.data.price}</p>
                  <p>Labor: ${item.data.labor}</p>
                </>
              )}
            </>
          }
        />
      </Card>
    </List.Item>
  );

  return (
    <div>
      <Row>
        <Col>
          <Space align="center">
            <Text>Filter by type:</Text>
            <Select value={selectedType} onChange={handleTypeChange} style={{ width: 200 }}>
              {PRODUCT_TYPES.map(type => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </Space>
        </Col>
      </Row>
      <List
        grid={{ gutter: 16, xs: 1, sm: 2, md: 3, lg: 4, xl: 5, xxl: 5 }}
        dataSource={filteredProducts}
        pagination={{
          current: currentPage,
          onChange: setCurrentPage,
          pageSize: PAGE_SIZE,
          total: filteredProducts.length,
          position: 'bottom',
          align: 'center',
          showSizeChanger: false,
          showTotal: total => `Total ${total} items`,
        }}
        renderItem={renderProductItem}
      />
    </div>
  );
};

const ProductsOfConfigurators: React.FC = () => {
  const { data = {}, loading, refetch } = useQuery(queries.GET_ALL_PRODUCTS_OF_CONFIGURATORS);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const { productsOfConfigurators } = data;

  return (
    <div>
      {loading ? (
        <Loader />
      ) : !productsOfConfigurators?.list ? (
        <div>
          <Text type="secondary">Products not found</Text>
        </div>
      ) : (
        <Row>
          <Col span={24}>
            <ProductList products={productsOfConfigurators.list} />
          </Col>
        </Row>
      )}
    </div>
  );
};

export default ProductsOfConfigurators;
